import { paginateSupabase } from '@/lib/supabase-paginate';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BUSINESS_UNITS, BusinessUnit } from '@/constants/businessUnits';
import {
  format,
  parseISO,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
} from 'date-fns';
import { convertAsOfForAccount, RateRow } from '@/lib/fx';
import {
  classifyExpenseToPnlLine,
  PNL_COGS_LINES,
  PNL_SGA_LINES,
} from '@/components/finance/reports/pnl-report/mapping';

const BASE_CCY = 'ETB';

export type BUViewPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly';

export interface ExpenseDetail {
  id: string;
  date: string;
  vendor: string | null;
  description: string | null;
  reference: string | null;
  costPosition: string | null;
  currency: string;
  originalAmount: number;
  baseAmount: number;
  portion: number;
  pct: number;
}
export interface BUPeriodCell {
  sales: number;
  opexByCategory: Record<string, number>;
  opexDetailsByCategory: Record<string, ExpenseDetail[]>;
  opexTotal: number;
  inflows: number;
  // Grant income (grants / cost reimbursements) that funds the Grantt BU. It is
  // this unit's revenue, so it is counted inside `sales` (not `inflows`); this
  // field tracks how much of `sales` came from it, for the sub-line.
  grantIncome: number;
  // Individual grant inflow records behind `grantIncome`, for the collapsible
  // Grant Income sub-line (Grantt BU only).
  grantIncomeDetails: ExpenseDetail[];
  // Sub-components of `sales` (currently populated for ChipChip BU only)
  salesBreakdown?: {
    chipchipSales: number;
    chipchipFees: number;
    b2bSales: number;
  };
}

// One row of the ChipChip accrual-P&L layout (CP1/CP2/COGS/SG&A). Rendered for
// the ChipChip BU in place of the flat opex list.
export interface ChipChipPnlRow {
  key: string;
  label: string;
  kind: 'header' | 'sub' | 'total' | 'margin' | 'line';
  values: Record<string, number>; // periodKey -> value
  details?: ExpenseDetail[]; // for collapsible 'line' rows
}

export interface BUPnLResult {
  periodKeys: string[];
  periodLabels: { key: string; label: string }[];
  byBU: Record<BusinessUnit, Record<string, BUPeriodCell>>;
  categoryNames: Record<string, string>;
  // ChipChip's expenses reshaped into the accrual-P&L layout.
  chipchipPnl: ChipChipPnlRow[];
}

function bucketKey(date: string | Date, period: BUViewPeriod): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (period === 'daily') return format(d, 'yyyy-MM-dd');
  if (period === 'weekly') return format(startOfWeek(d, { weekStartsOn: 4 }), 'yyyy-MM-dd');
  if (period === 'monthly') return format(d, 'yyyy-MM');
  return format(startOfQuarter(d), 'yyyy-MM-dd'); // quarterly
}

function buildPeriodKeys(dateFrom: string, dateTo: string, period: BUViewPeriod): string[] {
  const start = parseISO(dateFrom);
  const end = parseISO(dateTo);
  if (period === 'daily') return eachDayOfInterval({ start, end }).map((d) => format(d, 'yyyy-MM-dd'));
  if (period === 'weekly')
    return eachWeekOfInterval({ start, end }, { weekStartsOn: 4 }).map((d) => format(d, 'yyyy-MM-dd'));
  if (period === 'monthly') return eachMonthOfInterval({ start, end }).map((d) => format(d, 'yyyy-MM'));
  return eachQuarterOfInterval({ start, end }).map((d) => format(d, 'yyyy-MM-dd'));
}

function periodRange(key: string, period: BUViewPeriod): { start: string; end: string } {
  if (period === 'daily') return { start: key, end: key };
  if (period === 'weekly') {
    const d = parseISO(key);
    return {
      start: format(startOfWeek(d, { weekStartsOn: 4 }), 'yyyy-MM-dd'),
      end: format(endOfWeek(d, { weekStartsOn: 4 }), 'yyyy-MM-dd'),
    };
  }
  if (period === 'monthly') {
    const d = parseISO(key + '-01');
    return { start: format(startOfMonth(d), 'yyyy-MM-dd'), end: format(endOfMonth(d), 'yyyy-MM-dd') };
  }
  const d = parseISO(key);
  return { start: format(startOfQuarter(d), 'yyyy-MM-dd'), end: format(endOfQuarter(d), 'yyyy-MM-dd') };
}

function labelFor(key: string, period: BUViewPeriod): string {
  if (period === 'daily') return format(parseISO(key), 'MMM d');
  if (period === 'weekly') return `W ${format(parseISO(key), 'MMM d')}`;
  if (period === 'monthly') return format(parseISO(key + '-01'), 'MMM yyyy');
  const d = parseISO(key);
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${format(d, 'yyyy')}`;
}

export function useBusinessUnitPnL(
  dateFrom: string,
  dateTo: string,
  viewPeriod: BUViewPeriod = 'monthly',
  displayCurrency: 'ETB' | 'USD' = 'ETB'
) {
  return useQuery({
    queryKey: ['bu-pnl', dateFrom, dateTo, viewPeriod, displayCurrency],
    queryFn: async (): Promise<BUPnLResult> => {
      const periodKeys = buildPeriodKeys(dateFrom, dateTo, viewPeriod);
      const periodLabels = periodKeys.map((k) => ({ key: k, label: labelFor(k, viewPeriod) }));

      const empty = (): BUPeriodCell => ({
        sales: 0,
        opexByCategory: {},
        opexDetailsByCategory: {},
        opexTotal: 0,
        inflows: 0,
        grantIncome: 0,
        grantIncomeDetails: [],
        salesBreakdown: { chipchipSales: 0, chipchipFees: 0, b2bSales: 0 },
      });
      const byBU = Object.fromEntries(
        BUSINESS_UNITS.map((b) => [b.value, Object.fromEntries(periodKeys.map((k) => [k, empty()]))])
      ) as Record<BusinessUnit, Record<string, BUPeriodCell>>;
      const categoryNames: Record<string, string> = {};

      // Run all independent queries in parallel
      const [ratesRes, allocRes, expsRes, inflowsRes, ccSalesRes, ccFeesRes, b2bRes, zarelyRes, catsRes, bankAccountsRes] = await Promise.all([
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('exchange_rates')
            .select('currency_pair,rate,effective_date,bank_account_id,rate_type')
            .order('effective_date', { ascending: false })
            .range(from, to)
        ).then((data) => ({ data, error: null })),
        (supabase as any)
          .from('cost_position_bu_allocations')
          .select('category_id,business_unit,percentage')
          .limit(10000),
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('expenses')
            .select(
              'id,payment_date,amount,total_amount,net_payment,currency,business_unit,category_id,vendor_name,description,reference_number,payment_reference,bank_account_id,category:expense_categories(name,default_business_unit)'
            )
            .eq('payment_status', 'paid')
            .gte('payment_date', dateFrom)
            .lte('payment_date', dateTo)
            .order('payment_date', { ascending: true })
            .range(from, to)
        ).then((data) => ({ data, error: null })),

        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('cash_inflows')
            .select('inflow_date,amount,currency,business_unit,bank_account_id,category,source_name,reference_number,inflow_number')
            .gte('inflow_date', dateFrom)
            .lte('inflow_date', dateTo)
            .order('inflow_date', { ascending: true })
            .range(from, to)
        ).then((data) => ({ data, error: null })),
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('chipchip_daily_accepted_totals')
            .select('date,total_gmv')
            .gte('date', dateFrom)
            .lte('date', dateTo)
            .order('date', { ascending: true })
            .range(from, to)
        ).then((data) => ({ data, error: null })),
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('chipchip_daily_revenue_fees')
            .select('date,combined_total')
            .gte('date', dateFrom)
            .lte('date', dateTo)
            .order('date', { ascending: true })
            .range(from, to)
        ).then((data) => ({ data, error: null })),
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('b2b_sales_data')
            .select('sale_date,total_revenue')
            .gte('sale_date', dateFrom)
            .lte('sale_date', dateTo)
            .order('sale_date', { ascending: true })
            .range(from, to)
        ).then((data) => ({ data, error: null })),
        paginateSupabase<any>((from, to) =>
          (supabase as any)
            .from('shopify_orders')
            .select('created_at_shopify,total_price,currency,cancelled_at,financial_status')
            .gte('created_at_shopify', dateFrom)
            .lte('created_at_shopify', dateTo + 'T23:59:59')
            .is('cancelled_at', null)
            .order('created_at_shopify', { ascending: true })
            .range(from, to)
        ).then((data) => ({ data, error: null })),
        // Expense-category tree, for resolving each expense's cost-group +
        // position (needed to reshape ChipChip into the accrual P&L layout).
        (supabase as any)
          .from('expense_categories')
          .select('id,name,parent_id,category_type')
          .limit(10000),
        // Bank accounts: each carries the FX rate type its transactions are
        // converted with (bank_rate vs competitive_rate) — same policy as the
        // accrual P&L Report and the Cash Flow report.
        (supabase as any)
          .from('bank_accounts')
          .select('id,exchange_rate_type')
          .limit(10000),
      ]);

      const rates: RateRow[] = (ratesRes.data || []) as RateRow[];
      const accountRateType = new Map<string, string>(
        ((bankAccountsRes as any)?.data || []).map((a: any) => [a.id, a.exchange_rate_type || 'bank_rate'])
      );

      // Convert a source amount to the desired display currency, applied
      // per-transaction using the rate scoped to the row's bank_account_id when
      // available, then the rate type configured on that bank account
      // (bank_rate / competitive_rate), then account-agnostic bank_rate.
      const toDisplay = (
        amount: number,
        ccy: string | null | undefined,
        date: string,
        bankAccountId?: string | null
      ): number => {
        const from = (ccy || BASE_CCY).toUpperCase();
        const rateType = bankAccountId ? accountRateType.get(bankAccountId) || 'bank_rate' : 'bank_rate';
        // Step 1: normalise to ETB base (keeps all internal math consistent).
        let etb = amount;
        if (from !== BASE_CCY) {
          const c = convertAsOfForAccount(amount, from, BASE_CCY, date, rates, bankAccountId, rateType);
          if (!c) {
            console.warn(`[BU P&L] No FX ${from}->${BASE_CCY} as of ${date}; using raw`);
            etb = amount;
          } else {
            etb = c.converted;
          }
        }
        if (displayCurrency === 'ETB') return etb;
        // Step 2: ETB -> USD using the same per-transaction, account-aware rate.
        const c2 = convertAsOfForAccount(etb, BASE_CCY, 'USD', date, rates, bankAccountId, rateType);
        if (!c2) {
          console.warn(`[BU P&L] No FX ${BASE_CCY}->USD as of ${date}; using ETB`);
          return etb;
        }
        return c2.converted;
      };


      const allocMap = new Map<string, { bu: BusinessUnit; pct: number }[]>();
      for (const a of allocRes.data || []) {
        const arr = allocMap.get(a.category_id) || [];
        arr.push({ bu: a.business_unit as BusinessUnit, pct: Number(a.percentage) || 0 });
        allocMap.set(a.category_id, arr);
      }

      const exps = expsRes.data || [];
      // Per-expense allocations: inner-join on expenses for date filter (avoids large .in() list)
      const { data: expAllocs } = await (supabase as any)
        .from('expense_bu_allocations')
        .select('expense_id,business_unit,percentage,expense:expenses!inner(payment_date,payment_status)')
        .eq('expense.payment_status', 'paid')
        .gte('expense.payment_date', dateFrom)
        .lte('expense.payment_date', dateTo)
        .limit(10000);
      const expenseAllocMap = new Map<string, { bu: BusinessUnit; pct: number }[]>();
      for (const a of expAllocs || []) {
        const arr = expenseAllocMap.get(a.expense_id) || [];
        arr.push({ bu: a.business_unit as BusinessUnit, pct: Number(a.percentage) || 0 });
        expenseAllocMap.set(a.expense_id, arr);
      }

      // Category tree → resolve each expense's cost-group + position labels
      // (ported from useCashFlow's getExpenseLabel / getExpensePositionLabel).
      const categoryMap = new Map<string, { name: string; parent_id: string | null; category_type: string | null }>();
      for (const c of catsRes.data || []) {
        categoryMap.set(c.id, { name: c.name, parent_id: c.parent_id, category_type: c.category_type });
      }
      const getGroupLabel = (categoryId: string | null): string => {
        if (!categoryId) return 'Other Expenses';
        let current = categoryMap.get(categoryId);
        while (current) {
          if (current.category_type === 'cost_group') return current.name;
          if (current.parent_id) {
            const parent = categoryMap.get(current.parent_id);
            if (parent && parent.category_type === 'cost_group') return parent.name;
            current = parent || undefined;
          } else break;
        }
        return categoryMap.get(categoryId)?.name || 'Other Expenses';
      };
      const getPositionLabel = (categoryId: string | null): string =>
        (categoryId && categoryMap.get(categoryId)?.name) || 'Uncategorized';

      // ChipChip expenses reshaped into P&L lines (COGS / SG&A), keyed by line
      // label, each carrying per-period values + drill-down detail records.
      type ChipLine = { section: 'cogs' | 'sga'; values: Record<string, number>; details: ExpenseDetail[] };
      const chipchipLines = new Map<string, ChipLine>();
      const chipchipUnmapped: { values: Record<string, number>; details: ExpenseDetail[] } = { values: {}, details: [] };

      for (const e of exps) {
        const pk = bucketKey(e.payment_date, viewPeriod);
        const catId = e.category_id || 'uncategorized';
        categoryNames[catId] = e.category?.name || 'Uncategorized';
        // Same amount field as the accrual P&L Report / Cash Flow report.
        const rawAmount = Number(e.net_payment ?? e.total_amount ?? e.amount ?? 0);
        const total = toDisplay(rawAmount, e.currency, e.payment_date, e.bank_account_id);

        let splits: { bu: BusinessUnit; pct: number }[];
        const perExp = expenseAllocMap.get(e.id);
        if (perExp && perExp.length > 0) {
          splits = perExp;
        } else if (e.business_unit) {
          splits = [{ bu: e.business_unit as BusinessUnit, pct: 100 }];
        } else {
          const allocs = allocMap.get(catId);
          if (allocs && allocs.length > 0) {
            splits = allocs;
          } else {
            const fallback = (e.category?.default_business_unit || 'chipchip') as BusinessUnit;
            splits = [{ bu: fallback, pct: 100 }];
          }
        }

        for (const s of splits) {
          if (!byBU[s.bu]?.[pk]) continue;
          const cell = byBU[s.bu][pk];
          const portion = total * (s.pct / 100);
          cell.opexByCategory[catId] = (cell.opexByCategory[catId] || 0) + portion;
          cell.opexTotal += portion;
          const detail: ExpenseDetail = {
            id: e.id,
            date: e.payment_date,
            vendor: e.vendor_name || null,
            description: e.description || null,
            reference: e.payment_reference || e.reference_number || null,
            costPosition: e.category?.name || null,
            currency: (e.currency || BASE_CCY).toUpperCase(),
            originalAmount: rawAmount,
            baseAmount: total,
            portion,
            pct: s.pct,
          };
          const arr = cell.opexDetailsByCategory[catId] || (cell.opexDetailsByCategory[catId] = []);
          arr.push(detail);

          // ChipChip only: classify the expense into a P&L line for the
          // restructured section (COGS / SG&A), else collect as unmapped.
          if (s.bu === 'chipchip') {
            const cls = classifyExpenseToPnlLine(getGroupLabel(e.category_id), getPositionLabel(e.category_id));
            const target =
              cls
                ? chipchipLines.get(cls.line) ||
                  (() => {
                    const l: ChipLine = { section: cls.section, values: {}, details: [] };
                    chipchipLines.set(cls.line, l);
                    return l;
                  })()
                : chipchipUnmapped;
            target.values[pk] = (target.values[pk] || 0) + portion;
            target.details.push(detail);
          }
        }
      }

      // Cash inflows. For the Grantt BU, grant income (grants / cost
      // reimbursements / refunds) is the unit's revenue, so it is booked as
      // Sales rather than a separate Cash Inflows line. Everything else stays
      // in `inflows`.
      const GRANT_INCOME_CATS = new Set(['grant', 'cost_reimbursement', 'refund']);
      for (const i of inflowsRes.data || []) {
        const bu = (i.business_unit || 'chipchip') as BusinessUnit;
        const pk = bucketKey(i.inflow_date, viewPeriod);
        const cell = byBU[bu]?.[pk];
        if (!cell) continue;
        const amt = toDisplay(Number(i.amount || 0), i.currency, i.inflow_date, i.bank_account_id);
        if (bu === 'grantt' && GRANT_INCOME_CATS.has(i.category)) {
          cell.sales += amt;
          cell.grantIncome += amt;
          cell.grantIncomeDetails.push({
            id: `${i.inflow_number || i.inflow_date}-${cell.grantIncomeDetails.length}`,
            date: i.inflow_date,
            vendor: i.source_name || null,
            description: i.inflow_number || null,
            reference: i.reference_number || null,
            costPosition: null,
            currency: (i.currency || BASE_CCY).toUpperCase(),
            originalAmount: Number(i.amount || 0),
            baseAmount: amt,
            portion: amt,
            pct: 100,
          });
        } else {
          cell.inflows += amt;
        }
      }

      // ChipChip B2C sales (from Daily Accepted Orders -> GMV). Values are ETB.
      for (const r of ccSalesRes.data || []) {
        const pk = bucketKey(r.date, viewPeriod);
        const cell = byBU.chipchip[pk];
        if (!cell) continue;
        const amt = toDisplay(Number(r.total_gmv || 0), 'ETB', r.date, null);
        cell.sales += amt;
        if (cell.salesBreakdown) cell.salesBreakdown.chipchipSales += amt;
      }

      // ChipChip Other Revenue (Delivery + Packaging Fee) -> ChipChip BU. Values are ETB.
      for (const r of ccFeesRes.data || []) {
        const pk = bucketKey(r.date, viewPeriod);
        const cell = byBU.chipchip[pk];
        if (!cell) continue;
        const amt = toDisplay(Number(r.combined_total || 0), 'ETB', r.date, null);
        cell.sales += amt;
        if (cell.salesBreakdown) cell.salesBreakdown.chipchipFees += amt;
      }

      // B2B sales -> ChipChip. Values are ETB.
      for (const r of b2bRes.data || []) {
        const pk = bucketKey(r.sale_date, viewPeriod);
        const cell = byBU.chipchip[pk];
        if (!cell) continue;
        const amt = toDisplay(Number(r.total_revenue || 0), 'ETB', r.sale_date, null);
        cell.sales += amt;
        if (cell.salesBreakdown) cell.salesBreakdown.b2bSales += amt;
      }

      // Zarely Shopify sales -> Zarely BU
      for (const o of zarelyRes.data || []) {
        if (!o.created_at_shopify) continue;
        const dateStr = String(o.created_at_shopify).slice(0, 10);
        const pk = bucketKey(dateStr, viewPeriod);
        if (!byBU.zarely[pk]) continue;
        byBU.zarely[pk].sales += toDisplay(Number(o.total_price || 0), o.currency, dateStr, null);
      }

      // ── Assemble ChipChip's accrual-P&L layout ──────────────────────────────
      // Revenue is ChipChip's sales; CP1 = Revenue − Purchases; Gross Profit /
      // CP2 = CP1 − variable COGS. All lines reconcile to opexTotal via the
      // Other (Unmapped) line.
      const cc = byBU.chipchip;
      const pkMap = (fn: (k: string) => number): Record<string, number> =>
        Object.fromEntries(periodKeys.map((k) => [k, fn(k)]));
      const lineValues = (label: string): Record<string, number> =>
        chipchipLines.get(label)?.values ?? {};
      const lineDetails = (label: string): ExpenseDetail[] =>
        chipchipLines.get(label)?.details ?? [];

      const revenue = pkMap((k) => cc[k]?.sales || 0);
      const purchases = pkMap((k) => lineValues('Purchases')[k] || 0);
      const variableLabels = PNL_COGS_LINES.slice(1); // COGS minus Purchases
      const variableTotal = pkMap((k) =>
        variableLabels.reduce((s, lbl) => s + (lineValues(lbl)[k] || 0), 0)
      );
      const cp1 = pkMap((k) => revenue[k] - purchases[k]);
      const cp2 = pkMap((k) => cp1[k] - variableTotal[k]);
      const ratio = (num: Record<string, number>): Record<string, number> =>
        pkMap((k) => (revenue[k] ? num[k] / revenue[k] : 0));

      const chipchipPnl: ChipChipPnlRow[] = [
        { key: 'rev-header', label: 'REVENUE', kind: 'header', values: {} },
        { key: 'rev-chipchip', label: 'ChipChip', kind: 'sub', values: pkMap((k) => cc[k]?.salesBreakdown?.chipchipSales || 0) },
        { key: 'rev-fees', label: 'ChipChip (Delivery & Packaging Fees)', kind: 'sub', values: pkMap((k) => cc[k]?.salesBreakdown?.chipchipFees || 0) },
        { key: 'rev-b2b', label: 'B2B', kind: 'sub', values: pkMap((k) => cc[k]?.salesBreakdown?.b2bSales || 0) },
        { key: 'total-revenue', label: 'Total Revenue', kind: 'total', values: revenue },
        { key: 'cp1', label: 'Contribution Profit 1 (Revenue − Purchases)', kind: 'total', values: cp1 },
        { key: 'cp1-margin', label: 'CP1 Margin %', kind: 'margin', values: ratio(cp1) },
        { key: 'cogs-header', label: 'Operating Outflows - Cost of Good Sold', kind: 'header', values: {} },
        ...PNL_COGS_LINES.map((label): ChipChipPnlRow => ({
          key: `cogs-${label}`, label, kind: 'line', values: lineValues(label), details: lineDetails(label),
        })),
        { key: 'gross-profit', label: 'Gross Profit', kind: 'total', values: cp2 },
        { key: 'cp2', label: 'Contribution Profit 2 (CP1 − Variable Costs)', kind: 'total', values: cp2 },
        { key: 'cp2-margin', label: 'CP2 Margin %', kind: 'margin', values: ratio(cp2) },
        { key: 'sga-header', label: 'Operating Expenses (SG&A)', kind: 'header', values: {} },
        ...PNL_SGA_LINES.map((label): ChipChipPnlRow => ({
          key: `sga-${label}`, label, kind: 'line', values: lineValues(label), details: lineDetails(label),
        })),
        ...(periodKeys.some((k) => (chipchipUnmapped.values[k] || 0) !== 0)
          ? [{ key: 'other-unmapped', label: 'Other (Unmapped)', kind: 'line', values: chipchipUnmapped.values, details: chipchipUnmapped.details } as ChipChipPnlRow]
          : []),
      ];

      return { periodKeys, periodLabels, byBU, categoryNames, chipchipPnl };
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}
